import type { LocalInfoType, Response } from '@/apis/common.types';
import { queryProductRecommendData } from '@/apis/recommend/recommend';
import { SceneCode } from '@/apis/recommend/recommend.interface';
import { getParent, Instance, types } from 'mobx-state-tree';
import { withSetPropAction } from '../models/helpers/withSetPropAction';
export interface IRecommendStore {
  sceneCode: SceneCode;
  mainSkus?: string;
  bulkPrice?: string;
  c3Id?: string;
  activeTab?: number;
  recommendData: any[];
}

interface IProductResponse {
  paragraphs?: any[];
}

// 定义产品类型
const ProductType = types.frozen();

// 推荐子数据结构
const RecommendDataType = types
  .model('RecommendData', {
    slotId: types.maybeNull(types.string),
    slotName: types.string,
    page: types.number,
    pageSize: types.number,
    nowPage: types.number,
    localInfo: types.optional(types.frozen<LocalInfoType>(), {}),
    userInfoVO: types.optional(types.frozen<{ isMember?: boolean }>(), {}),
    productList: types.optional(types.array(ProductType), []),
    loading: types.boolean,
    noMores: types.boolean,
    errors: types.boolean,
  })
  .actions(withSetPropAction)
  .actions((self) => ({
    async loadProductData() {
      self.setProp('loading', true);
      const store = getParent<typeof RecommendStoreModel>(self, 2); //
      const { sceneCode, subSceneCode, mainSkus, bulkPrice, c3Id, channelPage } = store;

      if (!self || self.noMores) return self.setProp('loading', false);
      const { slotId, page, pageSize } = self;
      const response = (await queryProductRecommendData({
        sceneCode,
        subSceneCode,
        mainSkus,
        bulkPrice: bulkPrice || slotId || '',
        c3Id,
        channelPage,
        page,
        pageSize,
      })) as Response<IProductResponse>;

      const { code, data } = response;

      if (code === '200') {
        // 再次获取最新状态进行更新
        const paragraphs = data.paragraphs || [];
        // const paragraphs = mockList
        if (paragraphs.length === 0) {
          self.setProp('noMores', true);
        } else {
          // 使用 setProp 更新数组
          const newProductList = paragraphs.map((item) => ({ ...item }));
          self.setProp('productList', [...self.productList.slice(), ...newProductList]);
          self.setProp('page', self.page + 1);
          if (data.localInfo) {
            self.setProp('localInfo', data.localInfo);
          }
        }
      } else {
        self.setProp('errors', true);
      }
      self.setProp('loading', false);
    },
  }));

//推荐主root数据结构
export const RecommendStoreModel = types
  .model('RecommendStore')
  .props({
    sceneCode: types.optional(types.frozen<SceneCode>(), SceneCode.HOME_PAGE),
    subSceneCode: types.optional(types.string, ''),
    mainSkus: types.optional(types.string, ''),
    bulkPrice: types.optional(types.string, ''),
    c3Id: types.optional(types.string, ''),
    channelPage: types.optional(
      types.model({
        channelPageName: types.maybe(types.string),
        poolIdList: types.array(types.number),
      }),
      {
        channelPageName: undefined,
        poolIdList: [],
      },
    ),
    activeTab: types.optional(types.number, 0), //选中的tab下标
    recommendData: types.optional(types.array(RecommendDataType), []), //推荐数据的缓存数组
    minShowCount: types.optional(types.number, 0), //推荐组件最小展示商品数量
  })
  .views((self) => ({
    //获取到当前tab下标下的所有数据
    get currentRecommendData() {
      return self.recommendData[self.activeTab] || {};
    },
    get isHideRecommend() {
      return (
        !self.sceneCode ||
        self.recommendData.length === 0 ||
        (self.activeTab === 0 &&
          self.recommendData[self.activeTab].productList.length <= self.minShowCount)
      );
    },
  }))
  .actions(withSetPropAction)
  .actions((self) => ({
    initData(props: any) {
      self.sceneCode = props.sceneCode;
      self.subSceneCode = props.subSceneCode;
      self.mainSkus = props.mainSkus;
      self.bulkPrice = props.bulkPrice;
      self.c3Id = props.c3Id;
      self.activeTab = props.activeTab;
      self.channelPage = props.channelPage;
      self.recommendData = props.recommendData;
    },
    clearData() {
      self.recommendData.replace([]);
    },
  }));

export type RecommendStore = Instance<typeof RecommendStoreModel>;
